import {ConnectionUser} from '@lib/store';
import {WorkspaceReservation} from '@lib/store/src/data/timeslots/apiTypes';
import {groupBy} from '@lib/utils';
import {ReferenceLocation} from '../../../ui/hooks/timeslots/useConnectionsSchedule';

export const constructConnectionsOverview = (
  connectedUsers: Array<ConnectionUser>,
  connectionWorkspaceReservations: Array<WorkspaceReservation>,
  referenceLocation?: ReferenceLocation,
  groupLevel: 'building' | 'floor' | 'area' = referenceLocation?.areaId ? 'area' : 'building',
): Array<{
  locationName: string;
  isUserWorkspace: boolean;
  occupants: Array<{
    user: ConnectionUser;
    reservations: Array<WorkspaceReservation>;
  }>;
}> => {
  const relevantReservations = referenceLocation
    ? connectionWorkspaceReservations.filter((r) => r.workspace.buildingId === referenceLocation.buildingId)
    : connectionWorkspaceReservations;

  // We create groups per area or floor or building
  const getReservationGroupId = (loc: ReferenceLocation) => {
    if (groupLevel === 'area') {
      return loc.areaId ?? loc.floorId ?? loc.buildingId;
    }

    if (groupLevel === 'floor') {
      return loc.floorId ?? loc.buildingId;
    }

    return loc.buildingId;
  };

  const userWorkspaceId = referenceLocation ? getReservationGroupId(referenceLocation) : Symbol();

  // Index bookings by location
  const reservationsByWorkspace = groupBy(relevantReservations, (r) => getReservationGroupId(r.workspace));

  // Index connections by user id
  const connectionsByUserId = new Map(connectedUsers.map((c) => [c.id, c]));

  return Object.entries(reservationsByWorkspace)
    .map(([workspaceId, reservations]) => {
      const {workspace} = reservations[0];

      const byUserId = groupBy(reservations, (r) => r.owner.id);

      const floorName = 'floorName' in workspace ? workspace.floorName : undefined;
      const areaName = 'areaName' in workspace ? workspace.areaName : undefined;

      let locationName =
        groupLevel === 'area'
          ? floorName && areaName
            ? `${floorName}, ${areaName}`
            : floorName || ''
          : groupLevel === 'floor'
          ? floorName || ''
          : workspace.buildingName;

      return {
        isUserWorkspace: workspaceId === userWorkspaceId,

        locationName,

        occupants: Object.entries(byUserId)
          .flatMap(([userId, reservations]) => {
            const user = connectionsByUserId.get(userId);
            if (!user) {
              // TODO: this seems to now mostly happen because user reservations are included in the connections request
              console.warn(
                `Warning: received reservation for which user was not found in connections (in ${locationName}`,
              );
              return [];
            }

            return {
              user,
              reservations,
            };
          })
          .sort((a, b) => a.user.name.localeCompare(b.user.name)),
      };
    })
    .sort((a, b) => {
      if (a.isUserWorkspace) return -1;
      if (b.isUserWorkspace) return 1;

      return a.locationName.localeCompare(b.locationName);
    });
};
